import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Lock } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

import Config from "../../Config";
import { useAuth } from "../../utils/AuthContext";
import UserNavbar from "../../components/UserNavbar";
import Footer from "../../components/Footer";

const stripePromise = loadStripe(Config.STRIPE_PUBLISHABLE_KEY);

const includedFeatures = [
  "100 pages/day",
  "Access to the latest most powerful models",
  "Unlimited DR molfiles with full Markush & R-group support",
  "Full combinatorial expansion of Markush",
  "Full molecule inventory with text co-references & page mapping",
  "Fully mapped graphical reaction schemes",
  "JSON, CSV, ORD export formats",
  "1-Click Push integrations (coming soon)",
  "Standard support",
];

// Styling passed into Stripe's iframe so the card fields match the rest of the form
const stripeElementStyle = {
  base: {
    fontSize: "14px",
    color: "#111827",
    fontFamily: "inherit",
    "::placeholder": { color: "#9ca3af" },
  },
  invalid: { color: "#ef4444" },
};

const stripeElementClass =
  "w-full px-3 py-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-[#1d365d] focus-within:border-transparent";

const CheckoutForm: React.FC = () => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { refreshSubscriptionStatus } = useAuth();

  const [nameOnCard, setNameOnCard] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postcode, setPostcode] = useState("");
  const [country, setCountry] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) return;

    const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
      type: "card",
      card: cardNumberElement,
      billing_details: {
        name: nameOnCard,
        address: {
          line1: addressLine1,
          line2: addressLine2 || null,
          city,
          state,
          postal_code: postcode,
          country,
        },
      },
    });

    if (pmError) {
      setError(pmError.message ?? "Card error");
      setLoading(false);
      return;
    }

    const token = localStorage.getItem("access_token");
    const res = await fetch(
      `${Config.API_URL}/api/billing/create-subscription`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ payment_method_id: paymentMethod.id }),
      },
    );

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setError(data.detail ?? "Something went wrong. Please try again.");
      setLoading(false);
      return;
    }

    const { status, client_secret } = await res.json();

    if (status === "incomplete" && client_secret) {
      const { error: confirmError } =
        await stripe.confirmCardPayment(client_secret);
      if (confirmError) {
        setError(confirmError.message ?? "Payment confirmation failed");
        setLoading(false);
        return;
      }
    }

    // Refresh subscription status in context so ProtectedRoute allows access
    await refreshSubscriptionStatus();
    localStorage.removeItem("pendingPlan");
    navigate("/home/dashboard");
  };

  return (
    <form onSubmit={handleSubscribe} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Name on card
        </label>
        <input
          type="text"
          placeholder="Jane Smith"
          value={nameOnCard}
          onChange={(e) => setNameOnCard(e.target.value)}
          required
          disabled={loading}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1d365d] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Card number
        </label>
        <div className={stripeElementClass}>
          <CardNumberElement
            options={{ style: stripeElementStyle, showIcon: true }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Expiry date
          </label>
          <div className={stripeElementClass}>
            <CardExpiryElement options={{ style: stripeElementStyle }} />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            CVC
          </label>
          <div className={stripeElementClass}>
            <CardCvcElement options={{ style: stripeElementStyle }} />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Address Line 1
        </label>
        <input
          type="text"
          placeholder="Street address"
          value={addressLine1}
          onChange={(e) => setAddressLine1(e.target.value)}
          required
          disabled={loading}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1d365d] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Address Line 2{" "}
          <span className="text-gray-400 font-normal">(Optional)</span>
        </label>
        <input
          type="text"
          placeholder="Apartment, suite, unit, or building"
          value={addressLine2}
          onChange={(e) => setAddressLine2(e.target.value)}
          disabled={loading}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1d365d] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            City
          </label>
          <input
            type="text"
            placeholder="City or town"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            required
            disabled={loading}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1d365d] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            State/Province/County
          </label>
          <input
            type="text"
            placeholder="State, region, or province"
            value={state}
            onChange={(e) => setState(e.target.value)}
            disabled={loading}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1d365d] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            ZIP/Postal Code
          </label>
          <input
            type="text"
            placeholder="Postal code"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            required
            disabled={loading}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1d365d] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Country
          </label>
          <input
            type="text"
            placeholder="Country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            required
            disabled={loading}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1d365d] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={loading || !stripe}
        className="mt-2 w-full bg-[#1d365d] hover:bg-[#16294a] text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? "Processing..." : "Subscribe"}
      </button>
    </form>
  );
};

const UpgradeToIndividual: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <UserNavbar />
      <div className="flex-1 flex justify-center items-start py-12 px-4">
        <div className="w-full max-w-4xl">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="text-left mb-10">
            <h1 className="text-3xl font-semibold text-gray-900">
              Set up your Individual subscription
            </h1>
            <p className="mt-2 text-gray-500">
              Join for just £25 a month, and cancel anytime.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Plan summary */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm h-fit">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                What's included
              </h2>
              <ul className="space-y-3">
                {includedFeatures.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#1d365d] mt-0.5 shrink-0" />
                    <span className="text-sm text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-5 border-t border-gray-100 flex items-center justify-between">
                <span className="text-gray-700 font-medium">Total today</span>
                <span className="text-xl font-bold text-gray-900">£25.00</span>
              </div>
            </div>

            {/* Payment form — wrapped in Stripe Elements */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-6">
                <Lock className="w-5 h-5 text-gray-500" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Payment details
                </h2>
              </div>

              <Elements stripe={stripePromise}>
                <CheckoutForm />
              </Elements>

              <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-gray-400">
                <Lock className="w-3.5 h-3.5" />
                <span>Payments processed securely by Stripe</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default UpgradeToIndividual;
